import React, { useState } from 'react';
import { notification, Space } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import { IOrder, ISearchOrderReq, ISearchOrderRes } from '@/interfaces/order';
import { DeleteFilled } from '@ant-design/icons';
import { deleteOrder } from '@/adapters/order';
import { deleteOrder as manualDeleteOrder } from '@/adapters/manual-order';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { getOrderStatusColor, getOrderStatusName } from '@/helpers/orderHelper';
import { twMerge } from 'tailwind-merge';
import { EOrderStatus } from '@/enums/EOrder';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { isFullAccess, isManager } from '@/utils/permissionUtils';
import { cargoAction } from '@/utils/googleMapUtils';
import { EShippingType } from '@/enums/EShippingType';
import { getShippingTypeName } from '@/helpers/shippingType';

interface IProps {
  loading: boolean;
  criteria: ISearchOrderReq;
  order: ISearchOrderRes;
  onDelete: (id: number) => void;
  onEditData: (item: IOrder) => void;
  onRowSelected?: (rows: IOrder[]) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IOrder> | SorterResult<IOrder>[],
  ) => void;
  isManual?: boolean;
}

const _orderModal: IDataModal<IOrder> = {
  open: false,
};

const OrderTable = ({
  loading,
  criteria,
  order,
  onDelete,
  onTableChange,
  onRowSelected,
  isManual,
}: IProps) => {
  const [selectionType] = useState<'checkbox' | 'radio'>('checkbox');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const perFullAccess = isFullAccess();
  const perManager = isManager();
  dayjs.extend(relativeTime);
  // const navigate = useNavigate();
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IOrder>>(_orderModal);

  const onDeleteOrder = async () => {
    try {
      onDelete(Number(deleteModal?.id || 0));
      if (isManual) {
        await manualDeleteOrder(Number(deleteModal?.id));
      } else {
        await deleteOrder(Number(deleteModal?.id));
      }
      notification.success({
        message: 'ลบออเดอร์สำเร็จ',
      });

      setDeleteModal({ ..._orderModal });
    } catch (error) {
      console.log(error);
    }
  };

  const onCargo = async (numberPlate: string, lat?: string, lng?: string) => {
    await cargoAction(numberPlate, lat, lng);
  };

  const columns: TableProps<IOrder>['columns'] = [
    {
      sorter: true,
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold text-blue-700',
      width: 120,
      align: 'center',
      // fixed: 'left',
      render: (_, rec) => (
        <div className="flex flex-col ">
          <div
            onClick={() =>
              window.open(
                `/${isManual ? 'order-free-form' : 'order'}/${rec.id}`,
                '_blank',
              )
            }
            className={twMerge(
              'text-blue-700 underline cursor-pointer underline-offset-2',
              rec?.isDelete && 'text-red-500',
            )}
          >
            {_}
          </div>
        </div>
      ),
    },
    {
      sorter: true,
      title: 'วันที่ส่งสินค้า',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      align: 'center',
      className: 'font-medium text-black',
      render: (_, rec) => {
        const isLate = rec.status === EOrderStatus.PENDING;
        const isExpired = _ && dayjs(_).isBefore(dayjs(), 'day');
        const diffDay = _ && dayjs().diff(dayjs(_), 'day');
        const dateTwoDaysAgo = dayjs().subtract(diffDay, 'day').fromNow();
        return (
          <Space>
            <div className="flex flex-col">
              <div
                className={twMerge(
                  isLate && isExpired && 'line-through text-red-500',
                )}
              >
                {formatDate(_)}
              </div>
              {isLate && isExpired && (
                <div className="text-red-500">{dateTwoDaysAgo}</div>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
      render: (_, rec) => (
        <div className="truncate">
          {_}
          {rec.isSample && (
            <div className="text-red-500 !text-xs">(ตัวอย่าง)</div>
          )}
          {rec.cndnId && (
            <div
              className="text-red-500 !text-xs underline cursor-pointer underline-offset-2"
              onClick={() => window.open(`/cndn-note/${rec.cndnId}`, '_blank')}
            >
              {rec.cndnCode}
            </div>
          )}
          {rec.isReserved && (
            <div className="text-purple-600 !text-xs">จองสินค้า</div>
          )}
          {rec.isDelete && (
            <div className="text-red-500 !text-xs">{rec.deleteRemark}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Order No.',
      dataIndex: 'soNo',
      key: 'soNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'PO No.',
      dataIndex: 'poNo',
      key: 'poNo',
      align: 'center',
      width: 150,
    },
    {
      sorter: true,
      title: 'ยอดรวม',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      className: 'font-bold',
      align: 'right',
      width: 110,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      className: 'font-bold',
      align: 'right',
      width: 120,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'VAT',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      align: 'right',
      width: 120,
      className: 'font-bold',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ยอดสุทธิ',
      dataIndex: 'summary',
      key: 'summary',
      align: 'right',
      width: 120,
      className: 'font-bold text-blue-700',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'อัพเดทล่าสุด',
      dataIndex: 'updateDate',
      key: 'updateDate',
      width: 110,
      align: 'center',
      className: 'font-medium text-gray-500',
      render: _ => formatDate(_),
    },
    {
      title: 'ขนส่ง',
      dataIndex: 'numberPlate',
      key: 'numberPlate',
      width: 100,
      align: 'center',
      render: (_, rec) => {
        const isDispatch =
          rec.status == EOrderStatus.COMPLETED &&
          rec.transportation === EShippingType.TRUCK;
        return (
          <div
            className={twMerge(
              isDispatch &&
                'underline text-blue-700 cursor-pointer underline-offset-2',
            )}
            onClick={() =>
              isDispatch
                ? (rec?.keminTruckOrders?.licencePlateNumber &&
                    onCargo(
                      rec?.keminTruckOrders?.licencePlateNumber,
                      rec?.shippingAddress?.lat,
                      rec?.shippingAddress?.lng,
                    )) ||
                  undefined
                : undefined
            }
          >
            {rec.transportation === EShippingType.PUIS
              ? getShippingTypeName(rec.transportation)
              : isDispatch && rec?.keminTruckOrders?.licencePlateNumber}
          </div>
        );
      },
    },
    {
      title: 'Confirm Doc',
      dataIndex: 'isConfirm',
      key: 'isConfirm',
      width: 100,
      align: 'center',
      hidden: isManual,
      render: _ => (
        <div className={twMerge('rounded py-0.5', _ && ' bg-green-100')}>
          {_ ? 'Confirm' : '-'}
        </div>
      ),
    },
    {
      sorter: true,
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      hidden: isManual,
      render: (_, rec) => {
        const name = getOrderStatusName(
          _,
          rec.transportation,
          false,
          rec.interfaceErrorCode,
        );
        const status = getOrderStatusColor(_, rec.transportation);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5 px-1',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 40,
      hidden: !perFullAccess,
      render: (_, rec) => {
        const isShowDelete =
          (rec.status === EOrderStatus.PENDING && perFullAccess) ||
          ([
            EOrderStatus.APPROVED,
            EOrderStatus.RECEIVED,
            EOrderStatus.REJECTED,
          ].includes(rec.status) &&
            perManager);
        return (
          <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
            {isShowDelete && (
              <DeleteFilled
                className="text-red-700 px-1.5"
                onClick={() =>
                  setDeleteModal({
                    open: true,
                    item: rec,
                    id: rec.id,
                  })
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  const rowSelection: TableProps<IOrder>['rowSelection'] = {
    selectedRowKeys,
    onChange: (keys: React.Key[], selectedRows: IOrder[]) => {
      if (selectedRows.length > 5) {
        notification.warning({
          message: 'จำนวนเลือกเกินกำหนด',
          description: 'คุณสามารถเลือกได้สูงสุด 5 รายการเท่านั้น',
        });
        const limitedKeys = selectedRowKeys;
        const limitedRows = selectedRows.slice(0, 5);
        setSelectedRowKeys(limitedKeys);
        onRowSelected?.(limitedRows);
        return;
      }
      setSelectedRowKeys(keys);
      onRowSelected?.(selectedRows);
    },
  };
  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IOrder>
        rowSelection={
          isFullAccess() && !isManual
            ? { type: selectionType, ...rowSelection }
            : undefined
        }
        columns={columns}
        criteria={criteria}
        data={order}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1800}
        scroll
      />
      <DeleteConfirmModal
        content={`คุณต้องการยกเลิกออเดอร์ '${deleteModal?.item?.code}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._orderModal });
        }}
        handleOk={() => {
          onDeleteOrder();
        }}
        open={deleteModal?.open}
        title={`ออเดอร์ '${deleteModal?.item?.code}'`}
      />
    </div>
  );
};

export default OrderTable;
